/**
 * Phase 2A · Profile as Digital Home — the new sections.
 *
 * Renders the 11 Phase-2 sections (Awards, Academics, Recruiting, Media
 * Kit, Press, Events, Bookings, Fan Club, Newsletter, Merch, Game Film)
 * as a single continuous scroll on the athlete profile.
 *
 * Empty-section rendering is contextual:
 *   • Public viewers (anyone but the owner) simply see the section
 *     hidden when there's no content.
 *   • The signed-in owner sees an "Unlock +X score" prompt for each
 *     empty section that opens the inline editor drawer.
 */
import { Trophy, GraduationCap, Search, FileText, Newspaper,
         Calendar, DollarSign, Users, Mail, ShoppingBag, Film,
         Sparkles,
         Plus, ExternalLink } from 'lucide-react';

// Section registry — order = scroll order on the profile.
// iter616 · Bookings / Fan Club / Newsletter / Merch temporarily removed
// from the Network Profile at Jay's request — the empty-state prompts
// were creating visual noise on athletes who hadn't filled them out.
// We keep the block implementations + editor drawer wiring below so
// re-enabling them is a one-line change once the Money Layer ships.
export const DIGITAL_HOME_SECTIONS = [
  // iter124 · Athlete-authored milestones. These render at the very
  // top of The Story so the athlete's biggest moments land first, and
  // they also flow into the Career Timeline arc + Career Timeline
  // stack automatically (same `career_milestones` field).
  { id: 'milestones',  label: 'Milestones',    icon: Sparkles,     impact: 5 },
  { id: 'awards',      label: 'Awards',        icon: Trophy,       impact: 5 },
  { id: 'academics',   label: 'Academics',     icon: GraduationCap, impact: 5 },
  { id: 'recruiting',  label: 'Recruiting',    icon: Search,       impact: 10 },
  { id: 'media_kit',   label: 'Media Kit',     icon: FileText,     impact: 5 },
  { id: 'press',       label: 'Articles',      icon: Newspaper,    impact: 3 },
  { id: 'events',      label: 'Upcoming',      icon: Calendar,     impact: 3 },
  { id: 'game_film',   label: 'Game Film',     icon: Film,         impact: 3 },
];

const isPopulated = (athlete, id) => {
  // iter124 · Milestones live under `career_milestones` (that's the
  // schema the timeline arc reads). Everything else uses the section
  // id as the field key.
  const key = id === 'milestones' ? 'career_milestones' : id;
  const v = athlete?.[key];
  if (Array.isArray(v)) return v.length > 0;
  if (v && typeof v === 'object') return Object.values(v).some((x) => x != null && x !== '' && x !== false);
  return !!v;
};

/* ---------- Section shell -------------------------------------------- */
const Section = ({ id, label, icon: Icon, isOwner, empty, impact, onEdit, children }) => {
  if (empty && !isOwner) return null;
  return (
    <section id={`sec-${id}`} className="scroll-mt-24" data-testid={`digital-home-${id}`}>
      <div className="flex items-baseline gap-3 mb-4">
        <span className="w-8 h-8 rounded-full inline-flex items-center justify-center bg-blue-500/15 ring-1 ring-blue-400/40">
          <Icon className="w-4 h-4 text-blue-300" strokeWidth={2} />
        </span>
        <h2 className="font-display text-lg sm:text-xl uppercase tracking-tight text-white flex-1">{label}</h2>
        {isOwner && (
          <button
            type="button"
            onClick={() => onEdit(id)}
            className="text-[10px] uppercase tracking-widest text-blue-300 hover:text-blue-200 font-bold transition"
            data-testid={`edit-${id}`}
          >
            {empty ? 'Add' : 'Edit'}
          </button>
        )}
      </div>
      {empty ? (
        <button
          type="button"
          onClick={() => onEdit(id)}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white/[0.02] ring-1 ring-dashed ring-white/15 hover:ring-blue-400/40 hover:bg-white/[0.04] transition text-left"
          data-testid={`unlock-${id}`}
        >
          {/* Empty-state icon = the section's own icon (Trophy for
              Awards, GraduationCap for Academics, etc.) instead of a
              generic Plus — makes gaps feel like a specific promise. */}
          <span className="w-9 h-9 rounded-full bg-blue-500/15 ring-1 ring-blue-400/30 inline-flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-blue-300" strokeWidth={2} />
          </span>
          <div className="flex-1">
            <p className="text-sm text-white">Add {label.toLowerCase()}</p>
            <p className="text-[10px] uppercase tracking-widest text-blue-300 mt-0.5">+{impact} career score</p>
          </div>
          <Plus className="w-4 h-4 text-white/30 flex-shrink-0" />
        </button>
      ) : (
        <div>{children}</div>
      )}
    </section>
  );
};

/* ---------- Section renderers ---------------------------------------- */
// iter124 · Athlete-authored career milestones. Renders as a vertical
// timeline stack that mirrors the "CAREER TIMELINE" hero block below,
// so the athlete sees exactly what fans will see.
const MilestonesList = ({ items }) => (
  <ol className="space-y-2">
    {items.map((m, i) => (
      <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10">
        <span className="text-[10px] font-mono text-white/40 pt-0.5 flex-shrink-0 w-14">{m.year || '—'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-bold uppercase tracking-tight">{m.label || m.title}</p>
          {m.detail && <p className="text-xs text-white/60 mt-0.5">{m.detail}</p>}
          {m.team && <p className="text-[10px] uppercase tracking-widest text-blue-300/80 mt-1">{m.team}</p>}
        </div>
        {m.current && (
          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 text-emerald-300 flex-shrink-0">Current</span>
        )}
      </li>
    ))}
  </ol>
);

const AwardsList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((w, i) => (
      <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10">
        <span className="text-[10px] font-mono text-white/40 pt-0.5 flex-shrink-0 w-10">{w.year || '—'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-bold">{w.label}</p>
          {w.org && <p className="text-xs text-white/50 mt-0.5">{w.org}</p>}
        </div>
      </li>
    ))}
  </ul>
);

const AcademicsBlock = ({ data }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    {[
      { label: 'GPA', v: data.gpa },
      { label: 'Grad Year', v: data.graduation_year },
      { label: 'Major', v: data.major },
      { label: 'School', v: data.school },
      { label: 'SAT', v: data.sat },
      { label: 'ACT', v: data.act },
      { label: 'Class Rank', v: data.class_rank },
    ].filter((x) => x.v).map((x) => (
      <div key={x.label} className="p-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10">
        <p className="text-[10px] uppercase tracking-widest text-white/40">{x.label}</p>
        <p className="mt-1 font-display text-lg text-white leading-none">{x.v}</p>
      </div>
    ))}
  </div>
);

const RecruitingBlock = ({ data }) => (
  <div className="space-y-3">
    {data.status && (
      <div className="inline-flex px-3 py-1 rounded-full bg-blue-500/15 ring-1 ring-blue-400/40 text-xs uppercase tracking-widest text-blue-300 font-bold">
        {data.status}
      </div>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {[
        { label: 'Positions', v: (data.positions || []).join(', ') },
        { label: 'Height', v: data.height },
        { label: 'Weight', v: data.weight_lbs ? `${data.weight_lbs} lb` : null },
      ].filter((x) => x.v).map((x) => (
        <div key={x.label} className="p-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/40">{x.label}</p>
          <p className="mt-1 text-sm text-white">{x.v}</p>
        </div>
      ))}
    </div>
    <div className="flex flex-wrap gap-2">
      {data.contact_coach_email && (
        <a href={`mailto:${data.contact_coach_email}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-300 transition">
          Contact coach <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {data.film_link && (
        <a href={data.film_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white font-bold text-sm hover:bg-white/20 transition">
          Watch film <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  </div>
);

const MediaKitBlock = ({ data }) => (
  <div className="p-5 rounded-2xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] ring-1 ring-white/10">
    {data.one_liner && <p className="text-white/80 leading-relaxed italic mb-4">"{data.one_liner}"</p>}
    <div className="flex flex-wrap gap-2">
      {data.pdf_url && (
        <a href={data.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-300 transition">
          Download media kit <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {data.rates && (
        <span className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 ring-1 ring-white/10 text-white/70 text-sm">{data.rates}</span>
      )}
    </div>
  </div>
);

const PressList = ({ items }) => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {items.map((p, i) => (
      <li key={i}>
        <a
          href={p.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex flex-col rounded-2xl overflow-hidden bg-white/[0.03] ring-1 ring-white/10 hover:bg-white/[0.06] hover:ring-white/20 transition"
        >
          {/* Thumbnail — pulled from OpenGraph on save. Falls back to a
              gradient block so cards without thumbs still balance the
              grid instead of collapsing to text-only. */}
          <div className="relative aspect-[16/9] w-full bg-gradient-to-br from-blue-900/40 to-black overflow-hidden">
            {p.thumbnail && (
              <img
                src={p.thumbnail}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            )}
            {/* Source pill — bottom-right corner, tells you where the
                article lives at a glance. */}
            {(p.outlet || p.url) && (
              <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[9px] font-black uppercase tracking-widest text-white/90 ring-1 ring-white/15">
                {p.outlet || (() => {
                  try { return new URL(p.url).hostname.replace('www.', ''); }
                  catch { return 'Web'; }
                })()}
              </span>
            )}
          </div>
          <div className="p-3">
            <p className="text-sm text-white leading-snug line-clamp-3">{p.headline || p.url}</p>
            {p.date && <p className="mt-1 text-xs text-white/40">{p.date}</p>}
          </div>
        </a>
      </li>
    ))}
  </ul>
);

const EventsList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((e, i) => (
      <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10">
        <div className="flex-shrink-0 w-14 text-center">
          <p className="text-[10px] uppercase tracking-widest text-white/40">{new Date(e.date).toLocaleDateString(undefined, { month: 'short' })}</p>
          <p className="font-display text-xl text-white leading-none mt-1">{new Date(e.date).getDate()}</p>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-bold">{e.title}</p>
          {e.location && <p className="text-xs text-white/50 mt-0.5">{e.location}</p>}
        </div>
        {e.url && (
          <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest text-blue-300 hover:text-blue-200 font-bold self-center">
            Details →
          </a>
        )}
      </li>
    ))}
  </ul>
);

const BookingsBlock = ({ data }) => (
  <div className="p-5 rounded-2xl bg-white/[0.03] ring-1 ring-white/10">
    <p className="text-sm text-white/70 mb-3">Available for {(data.categories || []).join(' · ') || 'brand + speaking work'}.</p>
    <div className="flex flex-wrap items-center gap-3">
      {data.contact_email && (
        <a href={`mailto:${data.contact_email}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-300 transition">
          Book me <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {data.hourly_rate && <span className="text-xs text-white/50 font-mono">{data.hourly_rate}</span>}
      {data.turnaround_hours && <span className="text-xs text-white/50">~{data.turnaround_hours}h response</span>}
    </div>
  </div>
);

const FanClubBlock = ({ data }) => (
  <div className="p-5 rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent ring-1 ring-white/10">
    {data.tagline && <p className="text-white/80 leading-relaxed mb-4">{data.tagline}</p>}
    <div className="flex flex-wrap gap-2">
      {(data.platforms || []).map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/20 transition">
          {p.name}
        </a>
      ))}
    </div>
  </div>
);

const NewsletterBlock = ({ data }) => (
  <div className="p-5 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 flex flex-wrap items-center justify-between gap-3">
    <div>
      <p className="text-sm text-white font-bold">Get updates from me</p>
      <p className="text-xs text-white/50 mt-0.5">{data.provider ? `Powered by ${data.provider}` : 'Direct to your inbox'}</p>
    </div>
    {data.signup_url && (
      <a href={data.signup_url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-300 transition">
        Subscribe <Mail className="w-3.5 h-3.5" />
      </a>
    )}
  </div>
);

const MerchGrid = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {items.map((m, i) => (
      <a key={i} href={m.url} target="_blank" rel="noopener noreferrer"
        className="group block rounded-2xl overflow-hidden bg-white/[0.03] ring-1 ring-white/10 hover:ring-blue-400/40 transition">
        {m.image ? (
          <div className="aspect-square overflow-hidden bg-black">
            <img src={m.image} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
        ) : (
          <div className="aspect-square bg-gradient-to-br from-white/10 to-white/[0.02] flex items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-white/30" />
          </div>
        )}
        <div className="p-3">
          <p className="text-xs text-white truncate">{m.name}</p>
          {m.price && <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold mt-0.5">{m.price}</p>}
        </div>
      </a>
    ))}
  </div>
);

const GameFilmList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((g, i) => (
      <li key={i}>
        <a href={g.url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10 hover:bg-white/[0.06] transition">
          {g.thumb ? (
            <img src={g.thumb} alt="" className="w-16 h-10 rounded object-cover flex-shrink-0" loading="lazy" />
          ) : (
            <div className="w-16 h-10 rounded bg-white/10 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white">vs {g.opponent || 'opponent'}</p>
            {g.date && <p className="text-xs text-white/50 mt-0.5">{g.date} {g.duration ? `· ${g.duration}` : ''}</p>}
          </div>
          <ExternalLink className="w-3.5 h-3.5 text-white/40" />
        </a>
      </li>
    ))}
  </ul>
);

/* ---------- Anchor nav (sticky) -------------------------------------- */
export const DigitalHomeAnchorNav = ({ athlete, isOwner }) => {
  const visible = DIGITAL_HOME_SECTIONS.filter((s) => isOwner || isPopulated(athlete, s.id));
  if (visible.length === 0) return null;
  return (
    <nav
      className="sticky top-16 z-30 -mx-4 sm:mx-0 sm:px-0 py-2 backdrop-blur-md bg-black/70 border-y border-white/10 mb-8"
      data-testid="digital-home-anchor-nav"
    >
      {/* On <400px viewports the pills would clip on the left edge; the
          negative margin + generous horizontal padding + fade-mask keeps
          the scroll strip both edge-to-edge AND legible. */}
      <div
        className="flex gap-1.5 overflow-x-auto scrollbar-hide px-4 sm:px-0"
        style={{ WebkitMaskImage: 'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)', maskImage: 'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)' }}
      >
        {visible.map((s) => (
          <a key={s.id} href={`#sec-${s.id}`}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 ring-1 ring-white/10 text-[10px] uppercase tracking-widest text-white/70 hover:bg-white/10 hover:text-white transition font-bold">
            <s.icon className="w-3 h-3" strokeWidth={2.5} />
            {s.label}
          </a>
        ))}
      </div>
    </nav>
  );
};

/* ---------- Root component ------------------------------------------- */
const AthleteDigitalHomeSections = ({ athlete, isOwner, onEdit }) => {
  if (!athlete) return null;

  const renderer = {
    milestones: (v) => <MilestonesList items={v} />,
    awards:     (v) => <AwardsList items={v} />,
    academics:  (v) => <AcademicsBlock data={v} />,
    recruiting: (v) => <RecruitingBlock data={v} />,
    media_kit:  (v) => <MediaKitBlock data={v} />,
    press:      (v) => <PressList items={v} />,
    events:     (v) => <EventsList items={v} />,
    bookings:   (v) => <BookingsBlock data={v} />,
    fan_club:   (v) => <FanClubBlock data={v} />,
    newsletter: (v) => <NewsletterBlock data={v} />,
    merch:      (v) => <MerchGrid items={v} />,
    game_film:  (v) => <GameFilmList items={v} />,
  };

  return (
    <div className="space-y-10 py-6" data-testid="digital-home-sections">
      {/* iter127 · Owner-only disclaimer so athletes know their edit
          controls + placeholder rows are private and won't be seen
          by fans / recruiters until they add real content. */}
      {isOwner && (
        <p className="text-[10px] text-white/35 leading-relaxed" data-testid="owner-only-disclaimer">
          Only you can see the edit buttons and any content you add — fans and recruiters see just the finished sections.
        </p>
      )}
      {DIGITAL_HOME_SECTIONS.map((s) => {
        // Milestones data lives under `career_milestones`, everyone
        // else matches the section id 1:1.
        const dataKey = s.id === 'milestones' ? 'career_milestones' : s.id;
        const populated = isPopulated(athlete, s.id);
        return (
          <Section key={s.id} id={s.id} label={s.label} icon={s.icon}
            isOwner={isOwner} empty={!populated} impact={s.impact} onEdit={onEdit}>
            {populated && renderer[s.id]?.(athlete[dataKey])}
          </Section>
        );
      })}
    </div>
  );
};

export default AthleteDigitalHomeSections;
